import { Box, Show, useToken } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { IoMdEye } from 'react-icons/io';
import { IoLockOpen, IoPersonAdd } from 'react-icons/io5';
import { MdDelete, MdEdit, MdSupportAgent } from 'react-icons/md';
import { useQueryClient } from 'react-query';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftMessage from '@/components/Atoms/FaceliftMessage';
import FaceliftOverlayLoader from '@/components/Atoms/FaceliftOverlayLoader';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import DetailsView from '@/components/Templates/DetailsView';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import { useGetAccounts } from '@/hooks/service-hooks/accounts';
import { useInviteUser, useResetPassword } from '@/hooks/service-hooks/auth';
import {
	useDeleteUser,
	useGetUser,
	useGetUsers,
} from '@/hooks/service-hooks/users';
import usePermission from '@/hooks/usePermission';
import { getUsers } from '@/services/users';
import type { FilterType } from '@/types/table';
import type { User } from '@/types/users';
import { formatDate, formatDateTime } from '@/utils';
import { trackCoreClick } from '@/utils/analytics';
import { parseUserDataForDetailsModal } from '@/utils/detailsModalUtils';
import { generateCSVFromTable } from '@/utils/exportFiles';
import CreateEditUsersRoles from '../CreateEditUsersRoles';
import type { UsersRolesFormData } from '../UsersRolesForm/types';
import {
	columns,
	dropdownFilterOptions,
	getUserPresenceStatus,
	getUserStatus,
} from './manageUsersRoles';

const ManageUsersRolesTable = () => {
	const queryClient = useQueryClient();
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const [successMessage, setSuccessMessage] = useState('');
	const eventSourceRef = useRef<EventSource | null>(null);
	const reconnectTimeoutRef = useRef<number | null>(null);

	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | 'edit' | 'details' | 'delete' | 'supportTicket' | null;
		data: string | Partial<UsersRolesFormData> | null;
		userFullName?: string; // For support ticket subject
		clinicIds?: string[]; // For support ticket clinic pre-population
	}>({
		mode: null,
		data: null,
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: true,
		status: '',
		globalFilters: '[]',
		sort: '',
	});

	const {
		data,
		isFetching,
		isSuccess: isUsersFetchSuccess,
	} = useGetUsers(filters);
	const {
		data: userDetails,
		isFetching: isUserDetailsFetching,
		isSuccess: isUserDetailsFetchSuccess,
	} = useGetUser(
		'id',
		modalInformation.mode === 'details' || modalInformation.mode === 'edit'
			? (modalInformation.data as string)
			: null,
	);

	// Fetch clinic data when userDetails has clinic IDs or when support ticket mode has clinic IDs
	const clinicIds = useMemo(() => {
		// For support ticket mode, use the clinic IDs passed in modalInformation
		if (
			modalInformation.mode === 'supportTicket' &&
			modalInformation.clinicIds
		) {
			return modalInformation.clinicIds.filter(
				(id): id is string => typeof id === 'string' && id.length > 0,
			);
		}
		// For edit/details mode, use clinic IDs from userDetails
		if (userDetails?.clinics && Array.isArray(userDetails.clinics)) {
			return userDetails.clinics.filter(
				(id): id is string => typeof id === 'string' && id.length > 0,
			);
		}
		return [];
	}, [userDetails?.clinics, modalInformation.mode, modalInformation.clinicIds]);

	const { data: clinicsData, isFetching: isClinicsFetching } = useGetAccounts({
		page: 1,
		limit: 1000,
		enabled:
			clinicIds.length > 0 &&
			(modalInformation.mode === 'edit' ||
				modalInformation.mode === 'details' ||
				modalInformation.mode === 'supportTicket'),
		ids: clinicIds,
	});

	// Parse clinic data to { label, value } format
	const parsedClinics = useMemo(() => {
		if (!clinicsData?.items) return [];
		return clinicsData.items.map((clinic) => ({
			label: clinic.name,
			value: clinic.accountid,
		}));
	}, [clinicsData]);

	useEffect(() => {
		let isUnmounted = false;
		const baseURL = import.meta.env.VITE_USERS_URL;

		if (!baseURL) return;

		const connect = () => {
			if (isUnmounted || eventSourceRef.current) return;

			const eventSource = new EventSource(`${baseURL}/users/presence/stream`, {
				withCredentials: false,
			});
			eventSourceRef.current = eventSource;

			eventSource.onmessage = (event) => {
				try {
					const payload = JSON.parse(event.data) as {
						type: 'presence' | 'heartbeat';
						userId?: string;
						sessionStatus?: User['sessionStatus'];
						lastAccess?: string | null;
					};

					if (payload.type !== 'presence' || !payload.userId) {
						return;
					}

					queryClient.setQueriesData(['users'], (existingData) => {
						const typedData = existingData as
							| { items: User[]; meta: unknown }
							| undefined;

						if (!typedData?.items) {
							return existingData;
						}

						return {
							...typedData,
							items: typedData.items.map((item) =>
								item._id === payload.userId
									? {
											...item,
											sessionStatus: payload.sessionStatus || item.sessionStatus,
											lastAccess: payload.lastAccess || item.lastAccess,
										}
									: item,
							),
						};
					});
				} catch (error) {
					console.error('Failed to parse user presence event:', error);
				}
			};

			eventSource.onerror = (error) => {
				console.error('User presence SSE error:', error);
				eventSource.close();
				eventSourceRef.current = null;
				queryClient.invalidateQueries({ queryKey: ['users'] });

				if (!isUnmounted) {
					reconnectTimeoutRef.current = window.setTimeout(connect, 5000);
				}
			};
		};

		connect();

		return () => {
			isUnmounted = true;
			if (reconnectTimeoutRef.current) {
				window.clearTimeout(reconnectTimeoutRef.current);
			}
			eventSourceRef.current?.close();
			eventSourceRef.current = null;
		};
	}, [queryClient]);

	const {
		mutateAsync: deleteUser,
		isLoading: isDeletingUser,
		isSuccess: isDeletingUserSuccess,
	} = useDeleteUser(
		() => {
			setSuccessMessage('User deleted successfully');
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to delete user. Please try again.',
			);
		},
	);

	const {
		mutateAsync: inviteUser,
		isLoading: isInvitingUser,
		isSuccess: isInvitingUserSuccess,
	} = useInviteUser(
		(data) => {
			setSuccessMessage(data.message || 'User invited successfully');
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { error?: { message?: { message?: string } } } };
			};
			toast.error(
				apiError?.response?.data?.error?.message?.message ||
					'Failed to invite user. Please try again.',
			);
		},
	);

	const {
		mutateAsync: resetPassword,
		isLoading: isResettingPassword,
		isSuccess: isResettingPasswordSuccess,
	} = useResetPassword(
		(data) => {
			setSuccessMessage(data.message || 'Password reset successfully');
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { error?: { message?: { message?: string } } } };
			};
			toast.error(
				apiError?.response?.data?.error?.message?.message ||
					'Failed to reset password. Please try again.',
			);
		},
	);

	const transformedData = useMemo(() => {
		if (!data?.items) return [];

		return data.items.map((item) => {
			const status = getUserStatus(item?.status || false);
			const sessionStatus = getUserPresenceStatus(item?.sessionStatus);
			const username = `${item?.firstName} ${item?.lastName}`.trim();
			return {
				id: item?._id || '',
				username: (
					<FaceliftTruncatedTextWithTooltip text={username} maxLength={25} />
				),
				email: (
					<FaceliftTruncatedTextWithTooltip text={item?.email} maxLength={35} />
				),
				role: (
					<FaceliftTruncatedTextWithTooltip text={item?.role} maxLength={20} />
				),
				lastAccess: item?.lastAccess
					? formatDateTime(item?.lastAccess?.toString())
					: '-',
				sessionStatus: (
					<Box
						bg={sessionStatus.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={sessionStatus.label}
							maxLength={20}
						/>
					</Box>
				),
				portsAvailable: (
					<FaceliftTruncatedTextWithTooltip
						text={item?.portsAvailable || '-'}
						maxLength={20}
					/>
				),
				createdAt: formatDate(item.createdAt?.toString()),
				status: (
					<Box
						bg={status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={status.label}
							maxLength={20}
						/>
					</Box>
				),
			};
		});
	}, [data]);

	const transformedDataToExport = (data: User[]) => {
		if (!data) return [];

		return data.map((item) => {
			return {
				id: item?._id || '',
				username: `${item?.firstName} ${item?.lastName}`.trim(),
				email: item?.email,
				role: item?.role,
				lastAccess: item?.lastAccess
					? formatDate(item?.lastAccess?.toString())
					: '-',
				sessionStatus: getUserPresenceStatus(item?.sessionStatus).label,
				port: item?.portsAvailable || '-',
				createdAt: formatDate(item.createdAt?.toString()),
				status: item?.status ? 'enabled' : 'disabled',
				userType: item?.isExternal ? 'external' : 'internal',
			};
		});
	};

	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: true,
			status: status?.value,
			globalFilters: JSON.stringify(
				(globalFilters || [])?.map((filter) => {
					const filterObj = {
						key: filter.name === 'Clinics' ? '_ta_clinic_value' : filter.name,
						values: filter.options.map((option) => option.id as string),
					};
					return filterObj;
				}),
			),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	const handleCreate = () => {
		trackCoreClick('create_user_clicked', {
			source_module: 'manage_users_table',
		});
		setModalInformation({
			mode: 'create',
			data: null,
		});
	};

	const handleEdit = (userId: string) => {
		trackCoreClick('edit_user_clicked', {
			source_module: 'manage_users_table',
			target_user_id: userId,
		});
		setModalInformation({
			mode: 'edit',
			data: userId,
		});
	};

	useEffect(() => {
		if (
			modalInformation.mode === 'edit' &&
			userDetails &&
			typeof modalInformation.data === 'string' &&
			userDetails._id === modalInformation.data
		) {
			// Wait for clinics to load if clinic IDs exist, otherwise use empty array
			const shouldWaitForClinics = clinicIds.length > 0 && isClinicsFetching;
			if (shouldWaitForClinics) return;

			const userData = {
				id: userDetails?._id,
				firstName: userDetails?.firstName,
				lastName: userDetails?.lastName,
				email: userDetails?.email,
				role: userDetails?.role,
				port: userDetails?.portsAvailable === 'Payments MSL' ? 'PaymentsMSL' : userDetails?.portsAvailable,
				status: userDetails?.status ? 'enabled' : 'disabled',
				environments: userDetails?.environmentsAvailable,
				clinics: parsedClinics,
				userType: userDetails?.isExternal ? 'external' : 'internal',
				salesReps: userDetails?.salesRepsDetails?.map(
					(salesRep) => ({
						label: salesRep.name,
						value: salesRep.id,
					}),
				),
				consignments: userDetails?.locationsDetails?.map(
					(location) => ({
						label: location.name,
						value: location.id,
					}),
				),
				autoSyncClinics: userDetails?.autoSyncClinics,
				permissions: userDetails?.permissions,
				notificationTypes: userDetails?.notificationTypes,
				notificationsEnabled: userDetails?.notificationsEnabled || false,
			};
			setModalInformation({ mode: 'edit', data: userData });
		}
	}, [
		modalInformation.mode,
		modalInformation.data,
		userDetails,
		parsedClinics,
		clinicIds.length,
		isClinicsFetching,
	]);

	const handleDelete = (userId: string) => {
		setModalInformation({ mode: 'delete', data: userId });
	};

	const handleViewDetails = (userId: string) => {
		setModalInformation({
			mode: 'details',
			data: userId,
		});
	};

	const handleCloseModal = () => {
		queryClient.removeQueries(['user']);
		setModalInformation({ mode: null, data: null });
	};

	const handleCreateEditSuccess = (message: string) => {
		setSuccessMessage(message);
		handleCloseModal();
	};

	const handleInviteUser = async (id: string) => {
		const user = data?.items?.find((item) => item._id === id);
		if (user) {
			trackCoreClick('invite_user_clicked', {
				source_module: 'manage_users_table',
				target_user_id: id,
				managed_role: user.role,
				managed_port: user.portsAvailable,
			});
			const fullName = `${user.firstName} ${user.lastName}`.trim();
			await inviteUser({ email: user.email, fullName });
		}
	};

	const handleResetPassword = async (id: string) => {
		const user = data?.items?.find((item) => item._id === id);
		if (user) {
			trackCoreClick('reset_password_clicked', {
				source_module: 'manage_users_table',
				target_user_id: id,
				managed_role: user.role,
				managed_port: user.portsAvailable,
			});
			await resetPassword({ email: user.email });
		}
	};

	const exportAllAsCSV = async () => {
		try {
			const data = await getUsers(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(columns, transformedData, `Users-all`, true);
		} catch (error) {
			toast.error(`Failed to export CSV: ${error}`);
		}
	};

	const exportAllAsExcel = async () => {
		try {
			const data = await getUsers(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(columns, transformedData, `Users-all`, true);
		} catch (error) {
			toast.error(`Failed to export Excel: ${error}`);
		}
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					tableName="Manage Users & Roles"
					loading={isFetching}
					items={transformedData}
					columns={columns}
					topLabel="Manage Users & Roles"
					totalCount={data?.meta?.totalRecords || 0}
					onFilterApply={onFilterApply}
					searchable
					hideShowColumn
					expandable
					paginated
					fullscreen
					exportable
					bordered
					creatableFn={handleCreate}
					statusFilters={[
						{ label: 'All', value: '' },
						...dropdownFilterOptions,
					]}
					actions={[
						{
							label: 'Edit',
							icon: <MdEdit />,
							actionFn: (item: ColumnData) => {
								if (item.id) {
									handleEdit(item.id as string);
								}
							},
						},
						{
							label: 'Delete',
							icon: <MdDelete />,
							actionFn: (item: ColumnData) => {
								if (item.id as string) {
									handleDelete(item.id as string);
								}
							},
						},
						{
							label: 'Details',
							icon: <IoMdEye />,
							actionFn: (item: ColumnData) => {
								if (item.id as string) {
									handleViewDetails(item.id as string);
								}
							},
						},
						{
							label: 'Support Ticket',
							icon: <MdSupportAgent />,
							actionFn: (item: ColumnData) => {
								// Find the original user data to get full name and clinics
								const originalUser = data?.items?.find(
									(user) => user._id === item.id,
								);
								const userFullName = originalUser
									? `${originalUser.firstName} ${originalUser.lastName}`.trim()
									: '';

								// Extract clinic IDs from the original user data
								const userClinicIds = originalUser?.clinics || [];

								setIsFormDirty(false);
								setModalInformation({
									mode: 'supportTicket',
									data: item.id as string,
									userFullName: userFullName,
									clinicIds: userClinicIds,
								});
							},
							show: () => hasPermission(['SupportTickets:Create']),
						},
						{
							label: 'Invite User',
							icon: <IoPersonAdd />,
							actionFn: (item: ColumnData) => {
								if (item.id as string) {
									handleInviteUser(item.id as string);
								}
							},
						},
						{
							label: 'Reset Password',
							icon: <IoLockOpen />,
							actionFn: (item: ColumnData) => {
								if (item.id as string) {
									handleResetPassword(item.id as string);
								}
							},
						},
					]}
					csv
					excel
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
				/>
			</Box>

			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				title={modalInformation.mode === 'create' ? 'Add User' : 'Edit User'}
				sx={{
					width: '90vw',
					maxWidth: '95vw',
					maxHeight: '90vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
				isDirty={isFormDirty} // It is not negative because default value is empty so when the user changes the form, it will be dirty
			>
				<Show when={
					modalInformation.mode === 'edit' &&
						typeof modalInformation.data === 'string'
						? true
						: isUserDetailsFetching
				}
				fallback={
					<CreateEditUsersRoles
						mode={modalInformation.mode as 'create' | 'edit'}
						initialData={
							modalInformation.mode === 'edit' &&
								typeof modalInformation.data === 'object'
								? (modalInformation.data as Partial<UsersRolesFormData>)
								: undefined
						}
						isLoading={false}
						onSuccess={handleCreateEditSuccess}
						onIsDirtyChange={setIsFormDirty}
					/>
				}
				>
					<FaceliftDataLoader
						title="Loading user details..."
						description="Please wait while we fetch the user information."
					/>
				</Show>
				
				
			</FaceliftDialogWithExitConfirmation>

			<FaceliftDialog
				showBackBtn={true}
				title="Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '90vw',
					maxWidth: '95vw',
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
			>
				<Show when={isUserDetailsFetching}>
					<FaceliftDataLoader
						title="Loading user details..."
						description="Please wait while we fetch the user information."
					/>
				</Show>
				<Show when={!isUserDetailsFetching}>
					<DetailsView
						data={userDetails ? parseUserDataForDetailsModal(userDetails) : []}
						isLoading={false}
					/>
				</Show>
			
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation.mode === 'supportTicket'}
				onOpenChange={(open) => {
					if (!open) {
						setModalInformation({ mode: null, data: null });
						setIsFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic: parsedClinics.length > 0 ? parsedClinics[0] : null,
						type: { value: '953870007', label: 'Tech' },
						subject: modalInformation.userFullName || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setModalInformation({ mode: null, data: null });
						setIsFormDirty(false);
					}}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'supportTicket'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			<Show when={modalInformation.mode === 'delete'}>
				<FaceliftMessage
					timeout={10000}
					showCloseBtn={false}
					messageTitle="Delete User"
					message={`Are you sure you want to delete the user?`}
					error={true}
					footer={
						<Box display="flex" gap={2} width="100%" justifyContent="center">
							<FaceliftButton
								bg="transparent"
								color="inherit"
								border="1px solid"
								width={{ base: 'full', md: '100px' }}
								type="button"
								loading={isDeletingUser}
								onClick={() => {
									setModalInformation({ mode: null, data: null });
								}}
							>
								Cancel
							</FaceliftButton>
							<FaceliftButton
								width={{ base: 'full', md: '100px' }}
								type="button"
								loading={isDeletingUser}
								onClick={() => {
									setModalInformation({ mode: null, data: null });
									deleteUser(modalInformation.data as string);
								}}
							>
								Confirm
							</FaceliftButton>
						</Box>
					}
				/>
			</Show>

			<Show
				when={
					isUsersFetchSuccess ||
					isUserDetailsFetchSuccess ||
					isDeletingUserSuccess ||
					isInvitingUserSuccess ||
					isResettingPasswordSuccess
				}
			>
				<FaceliftMessage message={successMessage} timeout={5000} />
			</Show>

			{(isInvitingUser || isResettingPassword) && <FaceliftOverlayLoader />}
		</>
	);
};

export default ManageUsersRolesTable;
